import React, { useEffect } from 'react';
import uniqid from 'uniqid';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import FilterLabel from './common/FilterLabel';
import { breakpoints } from '../utils/styles';

const Container = styled.div`
  border-top: 1px solid var(--text-primary);

  body.theme-platinum & { border-color: var(--primary-b-hr); }
`;

const Heading = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;

  & > i {
    font-size: 1.5rem;
  }

  & > span {
    color: var(--primary);
    font-weight: 700;
  }

  & > i, & > span {
    cursor: pointer;
  }
`;

const FiltersList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const FilterItem = styled.li`
  align-items: center;
  border-bottom: 1px solid;
  cursor: pointer;
  display: flex;
  font-size: 1.25rem;
  gap: 0.25em;
  padding: 1.5em 0;

  ${props => props.checked && 'color: var(--primary);'}

  &:last-child {
    border-bottom: 0;
    padding-bottom: 0;
  }

  @media screen and (min-width: ${breakpoints.desktop}px) {
    font-size: 1rem;
  }
`;

const Checkbox = styled.i`
  font-size: 1.75em;
`;

const IconSvg = styled.svg`
  flex: 0 0 2em;
  margin: 0 1rem;
`;

const Icon = ({ src, checked }) => {
  const maskId = React.useMemo(() => `choices-filter-mask-${uniqid()}`, []);
  const color = checked ? 'var(--primary)' : 'var(--text-primary)';

  return (
    <IconSvg viewBox="0 0 100 100" aria-hidden="true" focusable="false">
      <defs>
        <mask id={maskId} x="0" y="0" width="100" height="100" maskUnits="userSpaceOnUse" style={{ maskType: 'alpha' }}>
          <image href={src} xlinkHref={src} x="0" y="0" width="100" height="100" preserveAspectRatio="xMidYMid meet" />
        </mask>
      </defs>

      <rect x="0" y="0" width="100" height="100" fill={color} mask={`url(#${maskId})`} />
    </IconSvg>
  );
};

const Button = styled.span`
  cursor: pointer;
  display: block;
  font-size: 1rem;
  line-height: 2;
  position: relative;
  width: 100%;
`;

const AllLabel = styled.span`
  font-weight: 700;
  margin-right: 1em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  body.theme-platinum & { font-weight: 400; }
`;

const Arrow = styled.i`
  position: absolute;
  right: 0;
  top: 0.5em;
`;

const slideInFromRight = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideOutToRight = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
`;

const FiltersWindowContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--page-bg);
  padding: var(--pad-x);
  z-index: 1000;
  animation: ${slideInFromRight} 0.3s ease-out;

  &.closing {
    animation: ${slideOutToRight} 0.3s ease-in forwards;
  }
`;

const FilterItemText = styled.span`
  margin-left: ${props => props.hasImage ? '0' : '0.75em'};
`;

class ChoicesFilter extends React.Component {
  constructor(props) {
    super(props);

    this.onChange = this.onChange.bind(this);
    this.onExpandedToggle = this.onExpandedToggle.bind(this);

    this.baseId = uniqid();
    this.state = {
      expanded: false,
    };

  }

  shouldComponentUpdate(nextProps, prevState) {
    return this.props.selection.join(',') !== nextProps.selection.join(',')
        || this.props.filters.join(',') !== nextProps.filters.join(',')
        || this.state.expanded !== prevState.expanded;
  }

  onChange(e) {
    const v = e.target.value;

    this.props.onChange(v);
  }

  onExpandedToggle() {
    this.setState(prevState => ({expanded: !prevState.expanded}));
  }

  render() {
    if (this.props.filters.length < 1) {
      return '';
    }

    return (
        <Container className="container py-3">
          {this.renderLabel()}
          {this.renderOptions()}
        </Container>
    );
  }

  renderLabel() {
    if (this.props.collapsible) {
      return (
          <Button width="100%" onClick={this.onExpandedToggle}>
            <FilterLabel className="mr-2">{this.props.label}:</FilterLabel>
            <AllLabel>{this.getSelectionLabel()}</AllLabel>
            <Arrow className={'fal mt-1 ml-auto ' + (this.state.expanded ? 'fa-angle-up' : 'fa-angle-down')}></Arrow>
          </Button>
      )
    }

    return <FilterLabel className="mb-3">{this.props.label}</FilterLabel>;
  }

  getSelectionLabel() {
    const result = [];
    const {filters, selection, attributes, partialSet} = this.props;
    const allSelected = selection.length === 0 || (partialSet !== true && selection.length === filters.length);

    filters.forEach(format => (selection.indexOf(format) > -1) && result.push(attributes[format].display_name));

    if (this.props.allLabelFn) {
      return this.props.allLabelFn(result, allSelected);
    }

    if (allSelected) {
      return this.props.allLabel || 'Todos';
    } else {
      return result.join(', ');
    }
  }

  renderOptions() {
    if (this.props.collapsible && !this.state.expanded) {
      return '';
    }

    if (this.props.collapsible) {
      return <FiltersWindow
        filters={this.props.filters}
        selection={this.props.selection}
        attributes={this.props.attributes}
        onSave={this.props.onSave}
        onClose={this.onExpandedToggle}
      />;
    }

    const {baseId, props} = this;
    const {filters, selection} = props;
    const type = props.multiple !== false ? 'checkbox' : 'radio';

    return (
        <div className="radio-check-cols fade-in">
          {props.all !== false && (
              <div className="custom-control custom-checkbox custom-control-inline mr-3 mb-2">
                <input className="custom-control-input" type={type} value="*" id={baseId + '-all'}
                       onChange={this.onChange}
                       checked={selection.length === 0}
                />
                <label className="custom-control-label" htmlFor={baseId + '-all'}>Todos</label>
              </div>
          )}
          {filters.map(filter => {
            const id = baseId + filter;
            if (!(filter in props.attributes)) return '';
            
            return (
                <div className="custom-control custom-checkbox custom-control-inline mr-3 mb-2" key={filter}>
                  <input className="custom-control-input" type={type} value={filter} id={id}
                         onChange={this.onChange}
                         checked={selection.indexOf(filter) > -1}
                  />
                  <label className="custom-control-label" htmlFor={id}>{props.attributes[filter].display_name}</label>
                </div>
            );
          })}
        </div>
    );
  }
}

const FiltersWindow = (props) => {
  const { attributes, filters } = props;
  const [selection, setSelection] = React.useState(props.selection);
  const [isClosing, setIsClosing] = React.useState(false);

  const onChange = (filter) => {
    const newSelection = selection.indexOf(filter) > -1 ? selection.filter(f => f !== filter) : [...selection, filter];
    setSelection(newSelection);
  }

  const onSubmit = () => {
    setIsClosing(true);
    setTimeout(() => {
      props.onSave(selection);
      props.onClose();
    }, 300);
  }

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      props.onClose();
    }, 300);
  }

  return <FiltersWindowContainer className={isClosing ? 'closing' : ''}>
    <Heading>
      <i className="fal fa-times" onClick={handleClose}></i>
      <span onClick={onSubmit}>Aplicar</span>
    </Heading>
    <div>
      <FiltersList>
        {filters.map(filter => {
          if (!(filter in attributes)) return '';
          const attribute = attributes[filter];
          const checked = selection.indexOf(filter) > -1;
          const hasImage = !!attribute.images && !!attribute.images.icon && !!attribute.images.icon.png;

          return (
            <FilterItem key={filter} checked={checked} onClick={() => onChange(filter)}>
              <Checkbox className={`fal fa-circle${checked ? '-check' : ''}`}></Checkbox>
              {hasImage && <Icon src={attribute.images.icon.png} checked={checked} />}
              <FilterItemText hasImage={hasImage}>{attribute.display_name}</FilterItemText>
            </FilterItem>
          );
        })}
      </FiltersList>
    </div>
  </FiltersWindowContainer>;
};

ChoicesFilter.propTypes = {
  all: PropTypes.bool,
  allLabel: PropTypes.string,
  allLabelFn: PropTypes.func,
  attributes: PropTypes.object.isRequired,
  collapsible: PropTypes.bool,
  filters: PropTypes.array.isRequired,
  label: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  partialSet: PropTypes.bool,
  selection: PropTypes.array.isRequired,
};

export default ChoicesFilter;